﻿import { atom, createStore } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";

import { NumberPortingFormProps } from "@/components/Checkout/Steps/NumberPorting/NumberPorting";
import { SimVerificationFormProps } from "@/components/Checkout/Steps/SimVerification/SimVerification";
import { BusinessPersonalDetailsFormFields } from "@/components/Portal/UserAccount/PersonalDetails/PersonalDetailsFormFields/PersonalDetailsFormFields";
import { SimTransferStates } from "@/hooks/useSimTransferStateInfo";
import {
  Basket,
  Product,
  SimCardDetail,
  SubscriberOrderResponse,
} from "@/services/model";

import { TransformedProduct } from "./tariffUtils";
import { WizardEntry } from "./wizardNavigation";

/**
 * Persisted DataContext (migrated from old DataContextProvider's manual localStorage usage)
 * Use createJSONStorage to ensure we use JSON storage and session/prod/local behaviour consistent with your other atoms.
 */
export type DataContextInterface = {
  basket?: Basket;
  personalDetails?: BusinessPersonalDetailsFormFields;
  selectedOption?: TransformedProduct;
  numberPorting?: NumberPortingFormProps;
  checkout?: object;
  simVerification?: SimVerificationFormProps;
  allowedMaxOnboardingStep?: number;
  isEmailVerified?: boolean;
  wizardEntry?: WizardEntry;
};

export const store = createStore();

/**
 * Read storage when the atom is created, not on first mount (Jotai 2's
 * default): the token atoms are read outside React (axios interceptors,
 * token refresh) and must hold the stored value right after a reload.
 */
export const readStorageOnInit = { getOnInit: true };

export const accessTokenAtom = atomWithStorage<string | null>(
  "accessToken",
  null,
  undefined,
  readStorageOnInit,
);

export const refreshTokenAtom = atomWithStorage<string | null>(
  "refreshToken",
  null,
  undefined,
  readStorageOnInit,
);

export const isAnonymousUserAtom = atomWithStorage<boolean>(
  "isAnonymousUser",
  false,
  undefined,
  readStorageOnInit,
);

export const setAuthAtom = atom(
  null,
  (
    _get,
    set,
    payload: {
      accessToken: string | null;
      refreshToken: string | null;
      isAnonymous?: boolean;
      isAgent?: boolean;
    },
  ) => {
    set(accessTokenAtom, payload.accessToken);
    set(refreshTokenAtom, payload.refreshToken);
    set(isAnonymousUserAtom, payload.isAnonymous ?? false);
    set(isAgentAtom, payload.isAgent ?? false);
    // Clear atomically in the same Jotai write-atom batch so no intermediate
    // render sees isAnonymousUser=false while the modal is still true.
    set(showsSessionExpiredModalAtom, undefined);
  },
);

export const clearAuthAtom = atom(null, (_get, set) => {
  set(accessTokenAtom, null);
  set(refreshTokenAtom, null);
  set(isAnonymousUserAtom, true);
  set(isAgentAtom, false);
});

export const isAgentAtom = atomWithStorage<boolean>(
  "isAgent",
  false,
  undefined,
  readStorageOnInit,
);

export const verifierAtom = atomWithStorage<string | undefined>(
  "verifier",
  undefined,
  undefined,
  readStorageOnInit,
);

export const initialDataContext: DataContextInterface = {
  allowedMaxOnboardingStep: 1,
  isEmailVerified: false,
};

export const dataContextAtom = atomWithStorage<DataContextInterface>(
  "dataContext",
  initialDataContext,
  createJSONStorage(() => localStorage),
  readStorageOnInit,
);

export const destinationAtom = atomWithStorage<string | undefined>(
  "destination",
  undefined,
  undefined,
  readStorageOnInit,
);

type TariffCheckoutPersistence =
  | {
      product?: Product;
      subscriberOrderResponse?: SubscriberOrderResponse;
      simDetail?: SimCardDetail;
      /**
       * Subscription name typed in the checkout, tied to its SIM: this state
       * is shared by all SIMs, and the name must not reach another one.
       */
      typedAlias?: { simId: number; value: string };
    }
  | undefined;

export const tariffCheckoutPaymentPersistenceAtom =
  atomWithStorage<TariffCheckoutPersistence>(
    "tariffCheckoutPaymentPersistence",
    undefined,
    undefined,
    readStorageOnInit,
  );

/** Drops the order data of a tariff checkout but keeps a typed name. */
export const keepTypedAlias = (
  prev: TariffCheckoutPersistence,
): TariffCheckoutPersistence =>
  prev?.typedAlias ? { typedAlias: prev.typedAlias } : undefined;

export const portalTariffProductIDAtom = atom<number | undefined>(undefined);

export enum AlertTypes {
  error,
  info,
  success,
  warning,
}

export interface AlertInterface {
  type: AlertTypes;
  title?: string;
  text: string;
}

export const alertListAtom = atom<AlertInterface[]>([]);

export type UserAccountState = {
  editingMail: boolean;
  editingPassword: boolean;
  editingPersonalData: boolean;
  editingPayment: boolean;
  editingSecurity: boolean;
};
export const userAccountStateAtomInitialState: UserAccountState = {
  editingMail: false,
  editingPassword: false,
  editingPersonalData: false,
  editingPayment: false,
  editingSecurity: false,
};
export const userAccountStateAtom = atom<UserAccountState>(
  userAccountStateAtomInitialState,
);

export const isEditingSimDetailsAtom = atom<boolean>(false);

/** True while a SIM-Details inline section (eSIM actions, network reset, …) is open. */
export const isSimDetailsSectionActiveAtom = atom<boolean>(false);

type DocumentsEditState = {
  billsSelecting: boolean;
  itemizedBillsSelecting: boolean;
};

export const documentsEditStateAtomInitialState: DocumentsEditState = {
  billsSelecting: false,
  itemizedBillsSelecting: false,
};

export const documentsEditStateAtom = atom<DocumentsEditState>(
  documentsEditStateAtomInitialState,
);

export const isListViewAtom = atomWithStorage<boolean | undefined>(
  "listView",
  false,
  undefined,
  readStorageOnInit,
);

export const basketIsUpdatedAtom = atom<boolean>(false);

export const esimTransferTypeAtom = atom<SimTransferStates>({});

export const showsSessionExpiredModalAtom = atom<boolean | undefined>(
  undefined,
);

export const showsBillingActionBlockedModalAtom = atom<boolean>(false);

export const twoFactorBackupCodesAtom = atom<number[]>([]);

export const phoneNumberAtom = atom<string>("");
